import { useEffect, useState } from "react";
import { Alert, Dropdown, Menu, MenuButton, MenuItem, Stack } from "@mui/joy";
import { useStytchB2BClient } from "@stytch/react/b2b";
import {
    type DiscoveredOrganization,
    exchangeSessionIntoOrganization,
    listActiveOrganizations,
    StepUpRequired,
} from "../../auth/projectExchange";
import { useStytchMemberSession, useStytchOrganization } from "../../hooks/stytch-compat";
import { Quiet } from "../documents/design/controls";

const SWITCH_FAILED_MESSAGE = "Could not switch project. Try again or sign out.";

/**
 * Recovery action for a project whose access cannot be admitted: lists the member's
 * other active projects and switches into the one picked. Runs outside the tenant
 * providers, so it holds plain state instead of a query and needs no document context.
 */
export function AccessRecoverySwitchProject() {
    const stytch = useStytchB2BClient();
    const { organization: currentOrg } = useStytchOrganization();
    const { session } = useStytchMemberSession();
    const [organizations, setOrganizations] = useState<readonly DiscoveredOrganization[]>([]);
    const [pending, setPending] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    const currentOrganizationId = currentOrg?.organization_id;

    useEffect(() => {
        let cancelled = false;
        listActiveOrganizations(stytch)
            .then((orgs) => {
                if (!cancelled) setOrganizations(orgs);
            })
            .catch((error) => {
                // Discovery is a convenience here; Retry and Sign out remain.
                console.error("[AccessRecovery] Failed to list projects:", error);
            });
        return () => {
            cancelled = true;
        };
    }, [stytch]);

    const others = organizations.filter(
        (org) => org.organization.organization_id !== currentOrganizationId,
    );
    if (others.length === 0) return null;

    const switchTo = async (organization: DiscoveredOrganization) => {
        if (pending) return;
        setPending(true);
        setFailure(null);
        try {
            await exchangeSessionIntoOrganization(stytch, {
                organization,
                currentOrganizationSlug: currentOrg?.organization_slug,
                sessionExpiresAt: session?.expires_at,
            });
            window.location.assign("/");
        } catch (error) {
            // A project that needs another sign-in method says which; the session stays.
            if (error instanceof StepUpRequired) setFailure(error.message);
            else {
                console.error("[AccessRecovery] Failed to switch organization:", error);
                setFailure(SWITCH_FAILED_MESSAGE);
            }
            setPending(false);
        }
    };

    return (
        <Stack gap={1}>
            <Dropdown>
                <MenuButton slots={{ root: Quiet }} disabled={pending} aria-haspopup="menu">
                    {pending ? "Switching…" : "Switch project"}
                </MenuButton>
                <Menu placement="bottom-start" sx={{ zIndex: 1300, maxHeight: "50vh" }}>
                    {others.map((org) => (
                        <MenuItem
                            key={org.organization.organization_id}
                            onClick={() => void switchTo(org)}
                        >
                            {org.organization.organization_name ||
                                org.organization.organization_slug}
                        </MenuItem>
                    ))}
                </Menu>
            </Dropdown>
            {failure && <Alert color="warning">{failure}</Alert>}
        </Stack>
    );
}
